import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
/*
 * Renders a JS Date for display as a string for users
 *
 * Only renders the date portion (not the time) of the date object
 *
 * \param date        The (optional) date to render
 * \param placeholder The placeholder to render if the date is undefined
 */
export const renderDate = (date?: Date, placeholder?: string): string => {
  if (date && date instanceof Date) {
    return `${dayjs(date).format('DD/MM/YYYY')}`;
  }
  return placeholder || '';
};

/*
 * Loads a JS date object from a string with 'YYYY-MM-DD'
 * format, returned by our FastAPI endpoint.
 *
 */
export const loadDate = (dateStr: string | undefined | null): Date | undefined | null => {
  if (dateStr === null || dateStr === undefined) {
    return dateStr;
  }

  // Parse the date string and create a Day.js object
  const dayjsDate = dayjs(dateStr);
  return dayjsDate.toDate();
};

/*
 * Serializes a JS date object to a string with 'YYYY-MM-DD'
 * format, which is what our FastAPI endpoints expect for date fields
 */
export const serializeDate = (date: Date | null | undefined): string | undefined | null => {
  // Null and undefined have different semantic meanings in our API requests
  // For update operations, undefined means "do not update this field", while
  // null means "set this field to null"
  if (date === null || date === undefined) {
    return date;
  }

  const d = dayjs(date);
  // An invalid Date formats to the literal string 'Invalid Date' in dayjs, which
  // then gets persisted and later crashes strptime (e.g. the holidays page /
  // invoice generator). Never emit that. Return undefined ("leave this field
  // alone"), NOT null — null means "clear it", and a cleared end_date is
  // open-ended, so a half-typed end date would silently extend an assignment.
  if (!d.isValid()) {
    return undefined;
  }

  return d.format('YYYY-MM-DD');
};

/*
 * Converts a JS date localized at the user's timezone
 * removing all time information from the date (hours, minutes etc.) by setting the date object's
 * time to midday
 */
export const localeDateToMidday = (date: dayjs.Dayjs | null | undefined): Date | null => {
  if (date) {
    // Convert the input date to UTC without changing its time
    const utcDate = date.toDate();

    // Set the time to midday
    utcDate.setHours(12, 0, 0, 0);
    return utcDate;
  }

  return null;
};

export const removeTZFromDate = (date: Date | undefined | null): number | null => {
  if (!date || !(date instanceof Date)) {
    return null;
  }
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return Date.UTC(year, month, day, 0, 0, 0, 0);
};

/**
 * Stable string key for a Date's local calendar day (YYYY-MM-DD).
 * Use this for identity-matching across save round-trips where one
 * side might be a picker-produced Date (LOCAL noon, via
 * `localeDateToMidday`) and the other a server-loaded Date (LOCAL
 * midnight, via `loadDate`). `Date.getTime()` differs across those
 * formats for the same calendar day; `dayKey` does not.
 */
export const dayKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** True iff two Dates fall on the same local calendar day. */
export const sameDay = (a: Date | null | undefined, b: Date | null | undefined): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return dayKey(a) === dayKey(b);
};

export function dayJsDate(): Date;
export function dayJsDate(year: number, month: number, day: number): Date;
export function dayJsDate(date: string | number | Date): Date;
export function dayJsDate(arg1?: string | number | Date, arg2?: number, arg3?: number): Date {
  // If no parameter is passed, return today in UTC
  if (arg1 === undefined) {
    return dayjs.utc().toDate();
  }
  if (arg1 === null) {
    // there is logic that relies on a null entry to Date being valid - under the hood this returns the first day of 1970
    // eslint-disable-next-line no-restricted-syntax
    return new Date(0);
  }
  // Accept year, month, day
  if (typeof arg1 === 'number' && typeof arg2 === 'number' && typeof arg3 === 'number') {
    // month is 0-based in JS Date
    // Use dayjs.utc for consistency
    const year = arg1.toString().padStart(4, '0');
    const month = (arg2 + 1).toString().padStart(2, '0'); // month is 0-based, so add 1
    const day = arg3.toString().padStart(2, '0');
    return dayjs.utc(`${year}-${month}-${day}`, 'YYYY-MM-DD', true).toDate();
  }
  // Accept numbers (timestamps)
  if (typeof arg1 === 'number') {
    return dayjs.utc(arg1).toDate();
  }
  const parsed = dayjs.utc(arg1);
  if (!parsed.isValid()) {
    // eslint-disable-next-line no-console
    console.warn('Invalid date input:', arg1);
  }
  return parsed.toDate();
}

/**
 * Generate an array of month names between two given dates, including the month of each of the dates.
 *
 * \param {Date} startDate - The starting date.
 * \param {Date} endDate - The ending date.
 *
 * \returns {string[]} An array of month names between the start and end dates.
 */
export function getMonthNamesBetweenDates(startDate: Date, endDate: Date): string[] {
  const months: string[] = [];

  if (endDate < startDate) {
    return months;
  }

  const currentStartDate = dayJsDate(startDate);

  // Change it to the first day of the month to avoid discarding months that not have an specific day like the 31st
  currentStartDate.setDate(1);

  while (currentStartDate.getTime() <= endDate.getTime()) {
    const monthName = currentStartDate.toLocaleString('en-GB', { month: 'long' });
    const formattedMonth = `${monthName}`;

    months.push(formattedMonth);

    // Move to the next month
    currentStartDate.setMonth(currentStartDate.getMonth() + 1);
  }

  return months;
}

/**
 * Generate an array of years between two given dates, inclusive.
 *
 * \param {Date} startDate - The starting date.
 * \param {Date} endDate - The ending date.
 *
 * \returns {string[]} An array of month names between the start and end dates.
 */
export function getYearNamesBetweenDates(startDate: Date, endDate: Date): string[] {
  const years = [];
  let currentYear = startDate.getFullYear();

  while (currentYear <= endDate.getFullYear()) {
    years.push(currentYear.toString());
    currentYear += 1;
  }

  return years;
}

/**
 * Convert a month name to its corresponding numerical representation.
 *
 * \param {string} monthName - The name of the month (case-insensitive).
 *
 * \returns {number} The numerical representation of the month (1 for January, 2 for February...)
 *  or 0 if not found.
 */
export function monthNameToNumber(monthName: string): number {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December',
  ];

  const monthIndex = monthNames.findIndex((name) => name.toLowerCase() === monthName.toLowerCase());

  return monthIndex + 1;
}

/**
 * Get the first and last day of a specific month and year.
 *
 * \param {number} year - The year (e.g., 2023).
 * \param {number} month - The month (0 for January, 1 for February, etc.).
 *
 * \returns {{ firstDay: Date, lastDay: Date }} An object containing the first and
 * last day of the specified month.
 *
 * \throws {Error} If the provided month is outside the valid range (0-11).
 */
export function getFirstAndLastDayOfMonth(year: number, month: number): { firstDayOfMonth: Date, lastDayOfMonth: Date } {
  // Ensure month is within the valid range (0-11)
  if (month < 0 || month - 1 > 11) {
    throw new Error('Invalid month value. Month should be in the range 0-11.');
  }

  const firstDayOfMonth = dayJsDate(year, month - 1, 1);
  const lastDayOfMonth = dayJsDate(year, month, 0);

  return { firstDayOfMonth, lastDayOfMonth };
}

/**
 * Shift a given end date forward by one day to make it inclusive for FullCalendar.
 *
 * FullCalendar treats the `end` date as exclusive in timeline views, which can result
 * in events visually appearing to end a day early. This function adjusts the end date
 * to ensure the last day is included in the calendar display.
 *
 * @param {string | Date} date - The original end date (as a Date object or ISO string).
 *
 * @returns {Date} A new Date object representing the inclusive end date.
 */
export function shiftEndDateToInclusive(date: string | Date): Date {
  const d = dayJsDate(date);
  d.setDate(d.getDate() + 1);
  return d;
}
